<script setup>
import { computed } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  crmData: {
    type: Object,
    required: true,
  },
  contactId: {
    type: [String, Number],
    required: true,
  },
});

defineEmits(['refresh']);

const contact = computed(() => props.crmData?.contact || {});
const stats = computed(() => props.crmData?.stats || {});
const deals = computed(() => props.crmData?.deals || []);
const appointments = computed(() => props.crmData?.appointments || []);
const timeline = computed(() => props.crmData?.timeline || []);

// Lead temperature display
const temperatureDisplay = computed(() => {
  const temp = contact.value?.lead_temperature;
  switch (temp) {
    case 'hot':
      return { label: 'Quente', color: 'bg-ruby-100 text-ruby-700', icon: '🔥' };
    case 'warm':
      return {
        label: 'Morno',
        color: 'bg-amber-100 text-amber-700',
        icon: '🌡️',
      };
    case 'cold':
    default:
      return { label: 'Frio', color: 'bg-blue-100 text-blue-700', icon: '❄️' };
  }
});

// Lead status display
const statusDisplay = computed(() => {
  const status = contact.value?.lead_status;
  const statusMap = {
    new: { label: 'Novo', color: 'bg-blue-100 text-blue-700' },
    contacted: { label: 'Contactado', color: 'bg-amber-100 text-amber-700' },
    qualified: { label: 'Qualificado', color: 'bg-green-100 text-green-700' },
    proposal: { label: 'Proposta', color: 'bg-purple-100 text-purple-700' },
    negotiation: { label: 'Negociação', color: 'bg-orange-100 text-orange-700' },
    won: { label: 'Ganho', color: 'bg-emerald-100 text-emerald-700' },
    lost: { label: 'Perdido', color: 'bg-ruby-100 text-ruby-700' },
  };
  return (
    statusMap[status] || {
      label: status || 'Novo',
      color: 'bg-n-alpha-2 text-n-slate-11',
    }
  );
});

// Format currency
const formatCurrency = value => {
  if (!value) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

// Format date
const formatDate = dateString => {
  if (!dateString) return '--';
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Contact Header Card -->
    <div
      class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
    >
      <div class="flex items-start gap-4">
        <Avatar
          :src="contact?.avatar || contact?.thumbnail"
          :name="contact?.full_name || contact?.name || 'User'"
          :size="56"
          rounded-full
        />
        <div class="flex-1 min-w-0">
          <h3 class="text-lg font-medium text-n-slate-12 truncate">
            {{ contact?.full_name || contact?.name || 'Contato' }}
          </h3>
          <p
            v-if="contact?.job_title || contact?.company_name_text"
            class="text-sm text-n-slate-11 truncate"
          >
            {{ contact?.job_title }}
            <span v-if="contact?.job_title && contact?.company_name_text">
              @
            </span>
            {{ contact?.company_name_text }}
          </p>
          <div class="flex flex-wrap items-center gap-2 mt-2">
            <span
              v-if="contact?.email"
              class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg bg-n-alpha-2 text-n-slate-11"
            >
              <span class="i-lucide-mail size-3.5" />
              {{ contact.email }}
            </span>
            <span
              v-if="contact?.phone_number"
              class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg bg-n-alpha-2 text-n-slate-11"
            >
              <span class="i-lucide-phone size-3.5" />
              {{ contact.phone_number }}
            </span>
          </div>
        </div>
      </div>

      <!-- Lead Score & Status Row -->
      <div class="flex items-center gap-3 pt-3 border-t border-n-weak">
        <div class="flex-1">
          <span class="text-xs font-medium text-n-slate-10 uppercase tracking-wide">
            Lead Score
          </span>
          <div class="flex items-center gap-2 mt-1">
            <div class="flex-1 h-2 rounded-full bg-n-alpha-2 overflow-hidden">
              <div
                class="h-full rounded-full bg-woot-500 transition-all duration-500"
                :style="{ width: `${contact?.lead_score || 0}%` }"
              />
            </div>
            <span class="text-sm font-semibold text-n-slate-12 tabular-nums">
              {{ contact?.lead_score || 0 }}/100
            </span>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <span
            class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg"
            :class="temperatureDisplay.color"
          >
            {{ temperatureDisplay.icon }} {{ temperatureDisplay.label }}
          </span>
          <span
            class="px-2.5 py-1 text-xs font-medium rounded-lg"
            :class="statusDisplay.color"
          >
            {{ statusDisplay.label }}
          </span>
        </div>
      </div>
    </div>

    <!-- Metrics Grid -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <div
        class="flex flex-col gap-1 p-4 rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
      >
        <span class="text-xs font-medium text-n-slate-10 uppercase tracking-wide">
          Total Compras
        </span>
        <span class="text-xl font-semibold text-n-slate-12 tabular-nums">
          {{ formatCurrency(contact?.total_purchases_value) }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ contact?.total_purchases_count || 0 }} compras
        </span>
      </div>

      <div
        class="flex flex-col gap-1 p-4 rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
      >
        <span class="text-xs font-medium text-n-slate-10 uppercase tracking-wide">
          Ticket Médio
        </span>
        <span class="text-xl font-semibold text-n-slate-12 tabular-nums">
          {{ formatCurrency(contact?.average_ticket) }}
        </span>
      </div>

      <div
        class="flex flex-col gap-1 p-4 rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
      >
        <span class="text-xs font-medium text-n-slate-10 uppercase tracking-wide">
          Lifetime Value
        </span>
        <span class="text-xl font-semibold text-emerald-600 tabular-nums">
          {{ formatCurrency(contact?.lifetime_value) }}
        </span>
      </div>

      <div
        class="flex flex-col gap-1 p-4 rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
      >
        <span class="text-xs font-medium text-n-slate-10 uppercase tracking-wide">
          Risco de Churn
        </span>
        <span
          class="text-xl font-semibold tabular-nums"
          :class="{
            'text-emerald-600': (contact?.churn_risk || 0) <= 30,
            'text-amber-600':
              (contact?.churn_risk || 0) > 30 && (contact?.churn_risk || 0) <= 70,
            'text-ruby-600': (contact?.churn_risk || 0) > 70,
          }"
        >
          {{ contact?.churn_risk || 0 }}%
        </span>
      </div>
    </div>

    <!-- Stats Row -->
    <div class="grid grid-cols-5 gap-3">
      <div
        class="flex flex-col items-center gap-1 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20"
      >
        <span class="text-2xl font-bold text-blue-600 tabular-nums">
          {{ stats?.total_conversations || 0 }}
        </span>
        <span class="text-xs font-medium text-blue-600/70">Conversas</span>
      </div>
      <div
        class="flex flex-col items-center gap-1 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20"
      >
        <span class="text-2xl font-bold text-emerald-600 tabular-nums">
          {{ stats?.total_appointments || 0 }}
        </span>
        <span class="text-xs font-medium text-emerald-600/70">Agendamentos</span>
      </div>
      <div
        class="flex flex-col items-center gap-1 p-3 rounded-xl bg-purple-50 dark:bg-purple-900/20"
      >
        <span class="text-2xl font-bold text-purple-600 tabular-nums">
          {{ stats?.total_products || 0 }}
        </span>
        <span class="text-xs font-medium text-purple-600/70">Produtos</span>
      </div>
      <div
        class="flex flex-col items-center gap-1 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20"
      >
        <span class="text-2xl font-bold text-amber-600 tabular-nums">
          {{ stats?.total_documents || 0 }}
        </span>
        <span class="text-xs font-medium text-amber-600/70">Documentos</span>
      </div>
      <div
        class="flex flex-col items-center gap-1 p-3 rounded-xl bg-n-alpha-2"
      >
        <span class="text-2xl font-bold text-n-slate-11 tabular-nums">
          {{ stats?.total_addresses || 0 }}
        </span>
        <span class="text-xs font-medium text-n-slate-10">Endereços</span>
      </div>
    </div>

    <!-- Deals & Appointments -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <!-- Active Deals -->
      <div
        class="flex flex-col rounded-xl bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
      >
        <div
          class="flex items-center gap-2 px-4 py-3 border-b border-n-weak bg-n-alpha-1"
        >
          <span class="i-lucide-briefcase size-4 text-n-slate-11" />
          <h4 class="text-sm font-medium text-n-slate-12">Deals Ativos</h4>
        </div>
        <div class="p-4">
          <div
            v-if="deals.length === 0"
            class="flex flex-col items-center justify-center py-6 text-n-slate-10"
          >
            <span class="i-lucide-briefcase size-8 mb-2 opacity-50" />
            <span class="text-sm">Nenhum deal encontrado</span>
          </div>
          <div v-else class="flex flex-col gap-2">
            <div
              v-for="deal in deals.slice(0, 5)"
              :key="deal.mapping_id"
              class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-1"
            >
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-n-slate-12 truncate">
                  {{ deal.funnel_name }}
                </p>
                <p class="text-xs text-n-slate-11">{{ deal.stage_name }}</p>
              </div>
              <div class="text-right ml-3">
                <p class="text-sm font-semibold text-emerald-600 tabular-nums">
                  {{ formatCurrency(deal.deal_value) }}
                </p>
                <p class="text-xs text-n-slate-10">{{ deal.probability }}%</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Upcoming Appointments -->
      <div
        class="flex flex-col rounded-xl bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
      >
        <div
          class="flex items-center gap-2 px-4 py-3 border-b border-n-weak bg-n-alpha-1"
        >
          <span class="i-lucide-calendar size-4 text-n-slate-11" />
          <h4 class="text-sm font-medium text-n-slate-12">
            Próximos Agendamentos
          </h4>
        </div>
        <div class="p-4">
          <div
            v-if="appointments.length === 0"
            class="flex flex-col items-center justify-center py-6 text-n-slate-10"
          >
            <span class="i-lucide-calendar size-8 mb-2 opacity-50" />
            <span class="text-sm">Nenhum agendamento encontrado</span>
          </div>
          <div v-else class="flex flex-col gap-2">
            <div
              v-for="appointment in appointments.slice(0, 5)"
              :key="appointment.id"
              class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-1"
            >
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-n-slate-12 truncate">
                  {{ appointment.title }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{ formatDate(appointment.scheduled_at) }}
                </p>
              </div>
              <span
                class="px-2 py-1 text-xs font-medium rounded-lg ml-3"
                :class="{
                  'bg-emerald-100 text-emerald-700':
                    appointment.status === 'confirmed',
                  'bg-amber-100 text-amber-700':
                    appointment.status === 'scheduled',
                  'bg-ruby-100 text-ruby-700': appointment.status === 'cancelled',
                  'bg-blue-100 text-blue-700': appointment.status === 'completed',
                }"
              >
                {{ appointment.status }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent Timeline -->
    <div
      class="flex flex-col rounded-xl bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
    >
      <div
        class="flex items-center gap-2 px-4 py-3 border-b border-n-weak bg-n-alpha-1"
      >
        <span class="i-lucide-activity size-4 text-n-slate-11" />
        <h4 class="text-sm font-medium text-n-slate-12">Atividade Recente</h4>
      </div>
      <div class="p-4">
        <div
          v-if="timeline.length === 0"
          class="flex flex-col items-center justify-center py-6 text-n-slate-10"
        >
          <span class="i-lucide-activity size-8 mb-2 opacity-50" />
          <span class="text-sm">Nenhuma atividade registrada</span>
        </div>
        <div v-else class="flex flex-col gap-3">
          <div
            v-for="event in timeline.slice(0, 5)"
            :key="event.id"
            class="flex items-start gap-3"
          >
            <div
              class="flex-shrink-0 size-8 rounded-full flex items-center justify-center"
              :class="{
                'bg-blue-100 text-blue-600': event.event_type === 'note',
                'bg-emerald-100 text-emerald-600': event.event_type === 'deal_won',
                'bg-ruby-100 text-ruby-600': event.event_type === 'deal_lost',
                'bg-purple-100 text-purple-600':
                  event.event_type === 'appointment_created',
                'bg-n-alpha-2 text-n-slate-11': ![
                  'note',
                  'deal_won',
                  'deal_lost',
                  'appointment_created',
                ].includes(event.event_type),
              }"
            >
              <span
                :class="{
                  'i-lucide-sticky-note': event.event_type === 'note',
                  'i-lucide-check-circle': event.event_type === 'deal_won',
                  'i-lucide-x-circle': event.event_type === 'deal_lost',
                  'i-lucide-calendar-plus':
                    event.event_type === 'appointment_created',
                  'i-lucide-activity': ![
                    'note',
                    'deal_won',
                    'deal_lost',
                    'appointment_created',
                  ].includes(event.event_type),
                }"
                class="size-4"
              />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12">{{ event.title }}</p>
              <p
                v-if="event.description"
                class="text-xs text-n-slate-11 mt-0.5 line-clamp-2"
              >
                {{ event.description }}
              </p>
              <p class="text-xs text-n-slate-10 mt-1">
                {{ formatDate(event.occurred_at) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
